import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, BookOpen } from "lucide-react";

const CentralMuseum = () => {
  const navigate = useNavigate();
  const museumImage = "/Indore/historical/centralmuseum0.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Museum",
    "name": "Central Museum Indore",
    "description": "Premier museum in Indore showcasing rich cultural heritage, historical artifacts, sculptures, and archaeological treasures of Madhya Pradesh",
    "image": museumImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "A.B. Road, Near GPO",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Tu-Su 10:00-17:00",
    "priceRange": "₹10",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.3",
      "reviewCount": "1200"
    }
  };

  const highlights = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Historical Artifacts",
      description: "Extensive collection of historical artifacts, sculptures, and archaeological findings from Madhya Pradesh"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Ancient Sculptures",
      description: "Beautiful stone sculptures and carvings from various periods of Indian history and culture"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cultural Heritage",
      description: "Displays showcasing the rich cultural heritage and traditions of Central India region"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Educational Tours",
      description: "Guided tours and educational programs for students and history enthusiasts"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹10 for Indians, ₹100 for Foreigners" },
    { label: "Timings", value: "10:00 AM - 5:00 PM (Closed Mondays)" },
    { label: "Photography", value: "Allowed with permission" },
    { label: "Best Time", value: "October to March" },
    { label: "Duration", value: "2-3 hours" },
    { label: "Facilities", value: "Library, Research center, Parking" }
  ];

  const museumCollections = [
    {
      title: "Archaeology Section",
      description: "Stone sculptures, inscriptions, and artifacts from ancient Indian civilizations",
      highlight: "Gupta period sculptures"
    },
    {
      title: "Anthropology Gallery", 
      description: "Tribal art, crafts, and cultural artifacts from various communities",
      highlight: "Tribal heritage displays"
    },
    {
      title: "Natural History",
      description: "Geological specimens, fossils, and natural history collections",
      highlight: "Dinosaur fossils"
    },
    {
      title: "Arms & Weapons",
      description: "Collection of ancient and medieval weapons, armor, and military artifacts",
      highlight: "Rajput and Maratha weapons"
    },
    {
      title: "Coin Gallery",
      description: "Numismatic collection featuring coins from various Indian dynasties",
      highlight: "Rare historical coins"
    },
    {
      title: "Manuscripts",
      description: "Ancient manuscripts, palm leaf texts, and historical documents",
      highlight: "Sanskrit manuscripts"
    }
  ];

  const nearbyAttractions = [
    { name: "Lal Bagh Palace", distance: "1 km", time: "5 mins" },
    { name: "Rajwada Palace", distance: "2 km", time: "8 mins" },
    { name: "Sarafa Bazaar", distance: "2.5 km", time: "10 mins" },
    { name: "Kanch Mandir", distance: "3 km", time: "12 mins" }
  ];

  const educationalPrograms = [
    {
      program: "School Tours",
      description: "Educational tours for students with expert guides",
      audience: "Students and educational institutions"
    },
    {
      program: "Research Programs",
      description: "Academic research opportunities and archival access",
      audience: "Researchers and scholars"
    },
    {
      program: "Cultural Events",
      description: "Regular exhibitions, lectures, and cultural programs",
      audience: "General public and art enthusiasts"
    },
    {
      program: "Workshop Series",
      description: "Hands-on workshops on archaeology and heritage conservation",
      audience: "History enthusiasts"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Central Museum Indore - Historical Artifacts & Cultural Heritage | Aaoghumen</title>
        <meta name="description" content="Visit Central Museum Indore with Aaoghumen - explore historical artifacts, ancient sculptures, tribal art, and archaeological treasures. Complete guide with timings, collections, and educational programs." />
        <meta name="keywords" content="central museum indore, indore museum timings, historical artifacts indore, archaeological museum indore, aaoghumen central museum, indore cultural heritage, museum collections indore, educational tours indore" />
        
        <meta property="og:title" content="Central Museum Indore - Historical & Cultural Heritage | Aaoghumen" />
        <meta property="og:description" content="Discover the rich cultural heritage of Central India at Central Museum Indore, featuring historical artifacts, sculptures, and archaeological treasures." />
        <meta property="og:image" content={museumImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/central-museum" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Central Museum Indore - Historical Heritage | Aaoghumen" />
        <meta name="twitter:description" content="Explore centuries of history and culture at Central Museum Indore with extensive collections and educational programs." />
        <meta name="twitter:image" content={museumImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/central-museum" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 overflow-hidden">
          <img
            src={museumImage}
            alt="Central Museum Indore historical artifacts"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Central Museum</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Journey through centuries of history and culture at Central India's premier museum, showcasing archaeological treasures, ancient artifacts, and the rich heritage of Madhya Pradesh.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.3 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>10 AM - 5 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹10 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Central Museum</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Central Museum Indore stands as one of Madhya Pradesh's most significant cultural institutions, preserving and showcasing the rich heritage of Central India. Established to promote understanding of the region's historical and cultural evolution, the museum houses an impressive collection of archaeological artifacts, sculptures, tribal art, and historical treasures spanning several millennia. The museum serves as a window into the diverse civilizations that have flourished in this region.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The museum's extensive collections include stone sculptures from the Gupta period, tribal artifacts showcasing the rich cultural diversity of Madhya Pradesh, numismatic collections, ancient manuscripts, and geological specimens. Each gallery tells a unique story of human civilization, artistic achievement, and cultural evolution. The museum also features rare fossils, medieval weapons, and inscriptions that provide insights into the political and social history of the region.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, explore the museum's treasures with expert guides who bring history to life through engaging narratives and detailed explanations. Our educational tours help visitors understand the significance of each artifact, the historical context of different periods, and the cultural continuity that connects ancient civilizations to modern times.
              </p>
            </div>
          </section>

          {/* Museum Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Museum Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Museum Collections */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Museum Collections & Galleries</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {museumCollections.map((collection, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-blue-600 mb-3">{collection.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{collection.description}</p>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{collection.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Educational Programs */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Educational Programs</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {educationalPrograms.map((program, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
                  <h3 className="text-xl font-bold text-blue-600 mb-3">{program.program}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{program.description}</p>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <p className="text-blue-800 font-medium text-sm">Target Audience: {program.audience}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Visitor Experience Guide */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Experience Guide</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Educational Tours</h3>
                <p className="text-gray-600 mb-4">Join guided tours to understand historical significance and cultural context.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Expert guides available in Hindi and English</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Special student group programs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Interactive sessions for children</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Research consultation available</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Photography Guidelines</h3>
                <p className="text-gray-600 mb-4">Capture memories while respecting museum artifacts and guidelines.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Photography permitted with permission</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>No flash photography near artifacts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Respect restricted areas and signages</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Commercial photography requires permit</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Best Time to Visit</h3>
                <p className="text-gray-600 mb-4">Plan your visit for optimal experience and comfortable exploration.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Morning hours (10 AM - 12 PM) less crowded</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Winter months (Oct-Mar) ideal weather</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Weekdays for peaceful exploration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Allow 2-3 hours for complete visit</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to A.B. Road, near GPO</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts, ₹40-100</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola easily available</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Dedicated parking area available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> A.B. Road, central location</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Central Museum Indore"</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Explore Historical Heritage at Central Museum</h2>
              <p className="text-xl mb-6">Discover centuries of culture and history with expert guides and educational programs</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>A.B. Road, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-blue-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Book Museum Tour
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-blue-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CentralMuseum;